/* pdp-fitment-gate.js — deferred-fitment Add to Cart gate
 * Inline customization layout + always-live Add to Cart + deferred fitment modal.
 * Gated behind window.__pdpFitmentGate (always on).
 *
 * The base page logic ships the Add to Cart buttons disabled and only enables
 * them after full fitment. This gate instead keeps them clickable (except on a
 * non-fit answer) and gates at the #product-form submit: if fitment isn't
 * complete the submit is blocked and a modal collects the fitment card + ACQ
 * first.
 */
(function () {
	"use strict";

	if (!window.__pdpFitmentGate) return;

	const LOG = function () {
		const args = Array.prototype.slice.call(arguments);
		args.unshift("wool [pdp-fitment-gate]");
		console.log.apply(console, args);
	};

	const ATC_BTN_SELECTOR =
		".add-to-cart, .add-to-cart-for-unpainted, .sticky-add-to-cart";
	const REVIEW_PROPERTY_NAME = "Fitment Check";
	const REVIEW_PROPERTY_VALUE = "Overridden";
	const REVIEW_REASON_PROPERTY_NAME = "Fitment Review Reason";
	const REVIEW_REASON_BODY_TYPE = "VIN/body type mismatch";
	const REVIEW_REASON_COUNTRY = "Manufacturing country mismatch";
	const REVIEW_REASON_EXCLUSION = "Fitment question exclusion";
	const REVIEW_REASON_FITMENT = "Vehicle/part fitment mismatch";
	const SIDE_FITMENT_PROPERTY_NAME = "properties[Position]";

	let modalOpen = false;
	let movedNodes = []; // { node, parent, nextSibling } for exact restoration
	let resolutionContinuation = null;
	let reviewAcknowledged = false;
	let lastFocusedElement = null;

	/* ── Readiness helpers ── */

	function getVehicle() {
		try {
			const a = JSON.parse(localStorage.getItem("searchTerms") || "[]");
			return Array.isArray(a) && a.length && a[0] && a[0].model ? a[0] : null;
		} catch (_) {
			return null;
		}
	}

	function getFitmentState() {
		const card = document.querySelector("[data-partify-fitment-card]");
		return card ? card.getAttribute("data-pfc-state") : null;
	}

	function fitmentPasses() {
		const state = getFitmentState();
		if (state === null || state === "") return false;
		return (
			state !== "fail" &&
			state !== "warn-submodel" &&
			state !== "loading" &&
			state !== "no-vehicle"
		);
	}

	function bodyTypeAcqMismatch() {
		const validator = window.PartifyVinBodyTypeAcq;
		if (validator && typeof validator.hasMismatch === "function") {
			try {
				return validator.hasMismatch() === true;
			} catch (_) {
				return false;
			}
		}
		if (validator && typeof validator.evaluate === "function") {
			try {
				const result = validator.evaluate();
				return !!(result && result.mismatch);
			} catch (_) {
				return false;
			}
		}

		const root = document.getElementById("fitment-questions-root");
		return !!(root && root.getAttribute("data-vin-body-type-mismatch") === "true");
	}

	function focusBodyTypeAcqMismatch() {
		const validator = window.PartifyVinBodyTypeAcq;
		if (validator && typeof validator.focusMismatch === "function") {
			try {
				if (validator.focusMismatch()) return;
			} catch (_) {}
		}
		const root = document.getElementById("fitment-questions-root");
		const group =
			root &&
			root.querySelector(
				'.fitment-type-select[data-vin-body-type-mismatch="true"]',
			);
		if (!group) return;
		try {
			group.scrollIntoView({ behavior: "smooth", block: "center" });
		} catch (_) {
			group.scrollIntoView();
		}
		const checked = group.querySelector("input[type='radio']:checked");
		if (checked && typeof checked.focus === "function") checked.focus();
	}

	function acqGroups() {
		const root = document.getElementById("fitment-questions-root");
		if (!root) return [];
		return Array.from(document.querySelectorAll(
			"#fitment-questions-root .fitment-type-select, [data-country-origin-fitment-root] .country-origin-fitment, #fitment-gate-mount .country-origin-fitment",
		));
	}

	function hasUnansweredAcq() {
		const groups = acqGroups();
		for (let i = 0; i < groups.length; i++) {
			if (!groups[i].querySelector("input[type='radio']:checked")) return true;
		}
		return false;
	}

	// True when there are no ACQ groups, or every group has a checked radio that
	// is not an exclusion. Works whether the ACQ DOM is on the page or in the modal.
	function acqAnswered() {
		const groups = acqGroups();
		if (!groups.length) return true;
		for (let i = 0; i < groups.length; i++) {
			const checked = groups[i].querySelector("input[type='radio']:checked");
			if (!checked) return false;
			if (checked.getAttribute("data-origin-type") === "exclusion") return false;
		}
		return true;
	}

	function hasAcqGroups() {
		return !!document.querySelector(
			"#fitment-questions-root .fitment-type-select, [data-country-origin-fitment-root] .country-origin-fitment, #fitment-gate-mount .country-origin-fitment",
		);
	}

	function hasOverrideEligibleAcqGroups() {
		const root = document.getElementById("fitment-questions-root");
		return !!(
			root &&
			root.querySelector(".fitment-type-select:not(.country-origin-fitment)")
		);
	}

	// Return the checked exclusion radio that makes the part incompatible. Body
	// type mismatches are classified separately to preserve incompatibility precedence.
	function getExclusionAnswer() {
		const root = document.getElementById("fitment-questions-root");
		if (!root) return null;
		const checked = document.querySelectorAll(
			"#fitment-questions-root .fitment-type-select input[type='radio']:checked, [data-country-origin-fitment-root] .country-origin-fitment input[type='radio']:checked, #fitment-gate-mount .country-origin-fitment input[type='radio']:checked",
		);
		for (let i = 0; i < checked.length; i++) {
			const group = checked[i].closest(".fitment-type-select");
			if (
				group &&
				group.dataset.bodyTypeAcq === "true" &&
				group.dataset.vinBodyTypeMismatch === "true"
			) {
				continue;
			}
			if (checked[i].getAttribute("data-origin-type") === "exclusion")
				return checked[i];
		}
		return null;
	}

	// A checked exclusion radio anywhere means the part does not fit.
	function hasExclusionAnswer() {
		return !!getExclusionAnswer();
	}

	// The canonical metaobject type and legacy fitment key for the Driver Side /
	// Passenger Side question are both `position`, which the renderer exposes as
	// the exact cart-property name `properties[Position]`. Match that identity,
	// never localized question text or answer copy.
	function hasSideFitmentHardStop() {
		const root = document.getElementById("fitment-questions-root");
		if (!root) return false;
		return !!root.querySelector(
			'.fitment-type-select input[type="radio"][name="' +
				SIDE_FITMENT_PROPERTY_NAME +
				'"]:checked[data-origin-type="exclusion"]',
		);
	}

	function selectedOptionAvailable(select) {
		const option = select && select.options[select.selectedIndex];
		return !!(
			select &&
			select.value &&
			option &&
			!option.disabled &&
			option.getAttribute("data-unavailable") !== "true" &&
			option.getAttribute("data-available") !== "false"
		);
	}

	function selectedQualityAvailable() {
		const radios = document.querySelectorAll(
			'input[type="radio"][name="quality_type"]',
		);
		if (!radios.length) return true;

		const checked = document.querySelector(
			'input[type="radio"][name="quality_type"]:checked',
		);
		return !!(
			checked &&
			!checked.disabled &&
			checked.getAttribute("data-available") !== "false" &&
			checked.getAttribute("data-unavailable") !== "true"
		);
	}

	function paintSelectionComplete() {
		const group = document.querySelector(".paint-options-checkbox-group");
		if (!group) return true;

		const checked = group.querySelector(
			'input[name="select_paint_option"]:checked',
		);
		if (!checked) return false;
		if (twoToneModeActive()) {
			if (checked.id === "checkbox-select-unpainted") return true;
			if (checked.id !== "checkbox-select-paint-option") return false;
			const twoToneSelect = document.getElementById("variant-selector");
			return selectedOptionAvailable(twoToneSelect);
		}
		if (checked.id !== "checkbox-select-paint-option") return true;

		const select = document.getElementById("variant-selector");
		return !!(select && !select.disabled && selectedOptionAvailable(select));
	}

	function getFormVariantId() {
		const form = document.getElementById("product-form");
		if (!form) return "";

		const idInput = form.querySelector('[name="id"]:not([disabled])');
		if (idInput && idInput.value) return String(idInput.value);

		const match = String(form.getAttribute("action") || "").match(
			/[?&]id=(\d+)/,
		);
		return match ? match[1] : "";
	}

	function hasSelectedAvailableVariant() {
		if (!selectedQualityAvailable() || !paintSelectionComplete()) return false;

		const select = document.getElementById("variant-selector");
		if (select && !select.disabled) return selectedOptionAvailable(select);

		const autoSelectData = document.getElementById("auto-select-variant-data");
		if (autoSelectData && !autoSelectData.getAttribute("data-variant-id"))
			return false;

		const checkedQuality = document.querySelector(
			'input[type="radio"][name="quality_type"]:checked',
		);
		if (checkedQuality && checkedQuality.getAttribute("data-variant-id"))
			return true;

		return !!(getFormVariantId() || window.selectedVariantId);
	}

	function canSubmitWithoutVehicle() {
		const fitmentState = getFitmentState();
		if (
			!fitmentState ||
			fitmentState === "loading" ||
			fitmentState === "fail" ||
			fitmentState === "warn-submodel"
		) return false;
		const countryOrigin = window.PartifyCountryOriginFitment;
		if (
			countryOrigin &&
			typeof countryOrigin.checkerVehiclePassesNativeFitment === "function" &&
			!countryOrigin.checkerVehiclePassesNativeFitment()
		) return false;
		return (
			!getVehicle() &&
			hasAcqGroups() &&
			acqAnswered() &&
			hasSelectedAvailableVariant()
		);
	}

	function twoToneModeActive() {
		return document.documentElement.dataset.partifyTwoTonePaint === "true";
	}

	function isReady() {
		if (window.__PARTIFY_TOUCHUP) return true;
		return (
			!bodyTypeAcqMismatch() &&
			!countryOriginVinIncomplete() &&
			!!getVehicle() &&
			fitmentPasses() &&
			acqAnswered()
		);
	}

	// Readiness and incompatibility are deliberately separate. Missing answers,
	// loading, no vehicle, and a missing submodel all mean "not ready yet"; only
	// a deterministic native/ACQ failure should be presented as incompatible.
	function isGenuinelyIncompatible() {
		if (window.__PARTIFY_TOUCHUP) return false;
		return (
			bodyTypeAcqMismatch() ||
			hasExclusionAnswer() ||
			getFitmentState() === "fail"
		);
	}

	function getReviewReason() {
		if (window.__PARTIFY_TOUCHUP) return "";
		if (bodyTypeAcqMismatch()) return REVIEW_REASON_BODY_TYPE;
		const exclusionAnswer = getExclusionAnswer();
		if (exclusionAnswer) {
			const answer = String(exclusionAnswer.value || "").trim();
			const group = exclusionAnswer.closest(".fitment-type-select");
			const reason =
				group && group.classList.contains("country-origin-fitment")
					? REVIEW_REASON_COUNTRY
					: REVIEW_REASON_EXCLUSION;
			return answer ? reason + ": " + answer : reason;
		}
		if (getFitmentState() === "fail") return REVIEW_REASON_FITMENT;
		return "";
	}

	function reviewPropertySelector(propertyName, activeOnly) {
		return (
			'input[name="properties[' +
			propertyName +
			']"]' +
			(activeOnly ? '[data-partify-fitment-review-active="true"]' : "")
		);
	}

	function restoreReviewPropertyOnForm(form, propertyName) {
		const input = form.querySelector(reviewPropertySelector(propertyName, true));
		if (!input) return;
		if (input.dataset.partifyFitmentReviewCreated === "true") {
			input.remove();
			return;
		}
		input.value = input.dataset.partifyFitmentReviewPreviousValue || "";
		delete input.dataset.partifyFitmentReviewActive;
		delete input.dataset.partifyFitmentReviewPreviousValue;
	}

	function resetReviewAcknowledgement() {
		reviewAcknowledged = false;
		const form = document.getElementById("product-form");
		if (!form) return;
		restoreReviewPropertyOnForm(form, REVIEW_PROPERTY_NAME);
		restoreReviewPropertyOnForm(form, REVIEW_REASON_PROPERTY_NAME);
	}

	function writeManagedReviewProperty(form, propertyName, value) {
		let input = form.querySelector(reviewPropertySelector(propertyName, false));
		if (!input) {
			input = document.createElement("input");
			input.type = "hidden";
			input.name = "properties[" + propertyName + "]";
			input.dataset.partifyFitmentReviewCreated = "true";
			form.appendChild(input);
		} else if (input.dataset.partifyFitmentReviewActive !== "true") {
			input.dataset.partifyFitmentReviewPreviousValue = input.value || "";
		}
		input.dataset.partifyFitmentReviewActive = "true";
		input.disabled = false;
		input.value = value;
	}

	function writeReviewPropertyToForm() {
		const form = document.getElementById("product-form");
		if (!form) return;
		writeManagedReviewProperty(
			form,
			REVIEW_PROPERTY_NAME,
			REVIEW_PROPERTY_VALUE,
		);
		const reason = getReviewReason();
		if (reason)
			writeManagedReviewProperty(form, REVIEW_REASON_PROPERTY_NAME, reason);
	}

	function requestResolution(continuation) {
		if (modalOpen) return false;
		if (isReady()) return false;
		resolutionContinuation =
			typeof continuation === "function" ? continuation : null;
		openModal();
		if (!modalOpen) resolutionContinuation = null;
		return modalOpen;
	}

	function canSubmitFromModal() {
		if (isReady()) return true;
		if (canAcknowledgeIncompatibility()) return true;
		return canSubmitWithoutVehicle();
	}

	function canAcknowledgeIncompatibility() {
		if (hasSideFitmentHardStop()) return false;
		if (!isGenuinelyIncompatible()) return false;
		if (countryOriginVinIncomplete() || hasUnansweredAcq()) return false;
		const fitmentState = getFitmentState();
		if (
			!fitmentState ||
			fitmentState === "loading" ||
			fitmentState === "warn-submodel"
		) return false;
		if (!paintSelectionComplete() || !hasSelectedAvailableVariant()) return false;
		if (getVehicle()) return fitmentState !== "no-vehicle";
		return hasOverrideEligibleAcqGroups() && fitmentState !== "fail";
	}

	function canProceed() {
		return isReady() || (reviewAcknowledged && canAcknowledgeIncompatibility());
	}

	function getReviewProperties() {
		if (!reviewAcknowledged || !canAcknowledgeIncompatibility()) return {};
		const properties = { [REVIEW_PROPERTY_NAME]: REVIEW_PROPERTY_VALUE };
		const reason = getReviewReason();
		if (reason) properties[REVIEW_REASON_PROPERTY_NAME] = reason;
		return properties;
	}

	function getReviewVinProperties() {
		if (!reviewAcknowledged || !canAcknowledgeIncompatibility()) return {};
		const api = window.PartifyCountryOriginFitment;
		if (api && typeof api.getVerifiedVinRequirement === "function") {
			try {
				const requirement = api.getVerifiedVinRequirement();
				const vin = requirement && requirement.applies !== false
					? String(requirement.vin || "").trim().toUpperCase()
					: "";
				if (vin) return { VIN: vin };
			} catch (_) {}
		}
		const formVin = getCanadaNativeFormVin(
			document.getElementById("product-form"),
		);
		return formVin ? { VIN: formVin } : {};
	}

	function modalValidationMessage() {
		if (canSubmitFromModal()) return "";
		if (hasSideFitmentHardStop()) {
			return "Please select the correct side before adding this item to cart.";
		}
		if (getFitmentState() === "warn-submodel") {
			return "Please update your vehicle with the required submodel before adding this item to cart.";
		}
		if (countryOriginVinMismatch()) {
			return "The VIN used to verify this part doesn’t match the vehicle currently selected. Use the same vehicle/VIN before adding this part to your cart.";
		}
		if (countryOriginVinIncomplete()) {
			return "Verify your vehicle's manufacturing country with a valid VIN before adding this item to cart.";
		}
		if (hasUnansweredAcq()) {
			return "Please answer the required fitment questions before adding this item to cart.";
		}
		if (twoToneModeActive() && !paintSelectionComplete()) {
			return "Choose the two-tone paint color for your main part before adding this item to cart.";
		}
		if (!hasSelectedAvailableVariant()) {
			return "Please select an available product option before adding this item to cart.";
		}
		return "Please confirm your fitment before adding this item to cart.";
	}

	/* ── Keep Add to Cart clickable unless a hard requirement is incomplete ── */

	// Country/manufacturing verification stays hard-blocking until its VIN result
	// is complete. Confirmed incompatibilities remain clickable so they can open
	// the review modal.
	function getCountryOriginVinRequirement() {
		const api = window.PartifyCountryOriginFitment;
		if (!api || typeof api.getVerifiedVinRequirement !== "function") return null;
		try {
			return api.getVerifiedVinRequirement();
		} catch (_) {
			return null;
		}
	}

	function countryOriginVinMismatch() {
		const requirement = getCountryOriginVinRequirement();
		return !!(requirement && requirement.reason === "active-vin-mismatch");
	}

	function countryOriginVinIncomplete() {
		const group = document.querySelector(
			".fitment-type-select.country-origin-fitment",
		);
		if (!group) return false;
		const requirement = getCountryOriginVinRequirement();
		return !requirement || (requirement.applies !== false && !requirement.vin);
	}

	function shouldBlockAtc() {
		return countryOriginVinIncomplete() && !countryOriginVinMismatch();
	}

	function isAdding(btn) {
		return (btn.textContent || "").trim() === "Adding…";
	}

	function enableAtcButtons() {
		if (shouldBlockAtc()) {
			document.querySelectorAll(ATC_BTN_SELECTOR).forEach(function (btn) {
				if (!isAdding(btn)) btn.disabled = true;
			});
			return;
		}
		// The base page may have hidden the button via hideAddToCartButton() (e.g.
		// wrong vehicle, no-vehicle, or paint variant not yet selected). Make it
		// visible so the user can click it and trigger the modal.
		if (typeof showAddToCartButton === "function") showAddToCartButton();
		document.querySelectorAll(ATC_BTN_SELECTOR).forEach(function (btn) {
			if (btn.disabled && !isAdding(btn)) btn.disabled = false;
		});
	}

	// The base page re-disables ATC in several places (load, quality/paint
	// transitions, fitment gate). Re-enable whenever it gets disabled while the
	// part still fits and we're not mid-add. Setting disabled=false fires no
	// further action (the handler only acts when disabled===true), so there is no loop.
	function watchAtcButtons() {
		document.querySelectorAll(ATC_BTN_SELECTOR).forEach(function (btn) {
			new MutationObserver(function () {
				if (isAdding(btn)) return;
				if (shouldBlockAtc()) {
					if (!btn.disabled) btn.disabled = true;
					return;
				}
				if (btn.disabled) {
					if (typeof showAddToCartButton === "function") showAddToCartButton();
					btn.disabled = false;
				}
			}).observe(btn, { attributes: true, attributeFilter: ["disabled"] });
		});
	}

	// Force all ACQ groups in #fitment-questions-root to the enabled state.
	// Mirrors setGroupEnabled(group, true) from product-page-fitment-btns.liquid,
	// which is not globally exposed. Called on init, after auto-select settles,
	// on fitment events, and by watchAcqGroups whenever a group gets re-disabled.
	function enableAllAcqGroups() {
		const root = document.getElementById("fitment-questions-root");
		if (!root) return;
		root.querySelectorAll(".fitment-type-select").forEach(function (group) {
			group.classList.remove("fitment-disabled");
			group.style.pointerEvents = "auto";

			const inner = group.querySelector(".fitment-options-container");
			if (inner) {
				inner.classList.remove("fitment-disabled");
				inner.style.pointerEvents = "auto";
			}

			group.querySelectorAll("input[type='radio']").forEach(function (r) {
				r.disabled = false;
				r.classList.remove("fitment-disabled");
				r.classList.remove("individual-fitment-select-disabled");
			});

			group.querySelectorAll(".fitmentText").forEach(function (l) {
				l.style.color = "#1D1D1D";
				l.classList.remove("fitment-disabled");
				l.classList.remove("individual-fitment-label-disabled");
			});

			group.querySelectorAll(".option-title-fitment").forEach(function (t) {
				t.style.color = "#e61b24";
			});

			const modalBtn = group.querySelector(".fitment-button-description");
			if (modalBtn) {
				modalBtn.disabled = false;
				modalBtn.style.opacity = "1";
			}
		});
	}

	// Watch #fitment-questions-root for any runtime disabling (class or disabled
	// attribute changes) and immediately re-enable. The debounce timer ensures
	// the loop naturally terminates once all groups are already enabled.
	function watchAcqGroups() {
		const root = document.getElementById("fitment-questions-root");
		if (!root) return;
		let timer = null;
		new MutationObserver(function () {
			clearTimeout(timer);
			timer = setTimeout(enableAllAcqGroups, 0);
		}).observe(root, { subtree: true, attributes: true, attributeFilter: ["class", "disabled"] });
	}

	// Clear the initial "greyed / step-locked" render state that the base page
	// only clears on fitment success: the "Discounted Paint" / in-stock badges
	// (rendered with a `disabled` class) and the help/modal buttons (e.g. "Not
	// sure which quality?"). The disable* functions are no-ops while the gate is
	// on, so nothing re-greys them.
	function normalizeCustomizationUI() {
		document
			.querySelectorAll(".supplier-preferred-badge.disabled, .quality-in-stock-badge.disabled")
			.forEach(function (el) {
				el.classList.remove("disabled");
			});
		if (typeof enableQualityDescriptionBtn === "function") enableQualityDescriptionBtn();
		// Enable paint-code help button directly — calling enablehowToFindPaintCodeBtn() triggers
		// enablePaintOptionRadioBtns() → initPaintOptionDefaults() → dispatches a bubbling
		// 'change' on the unpainted radio → the page's paint handlers loop infinitely → stack overflow.
		const paintCodeHelpBtn = document.getElementById(
			"how-to-find-your-paint-code-vindecoder"
		);
		if (paintCodeHelpBtn) paintCodeHelpBtn.disabled = false;
		if (typeof enableGetPaintCodeUsingVin === "function") enableGetPaintCodeUsingVin();
		if (typeof enableSelectVinVariantBtn === "function") enableSelectVinVariantBtn();
	}

	/* ── Modal move in / out ── */

	function fitmentNodes() {
		// Move BOTH the fitment card and the ACQ container (which wraps
		// #fitment-questions-root). Filter out anything missing.
		const nodes = [];
		const card = document.querySelector("[data-partify-fitment-card]");
		const country = document.querySelector("[data-country-origin-fitment-root] .country-origin-fitment");
		const acq = document.querySelector(".required-questions-container");
		if (card) nodes.push(card);
		if (country) nodes.push(country);
		if (acq) nodes.push(acq);
		return nodes;
	}

	function openModal() {
		const modal = document.getElementById("fitment-gate-modal");
		const mount = document.getElementById("fitment-gate-mount");
		if (!modal || !mount || modalOpen) return;
		resetReviewAcknowledgement();
		lastFocusedElement = document.activeElement;

		movedNodes = [];
		fitmentNodes().forEach(function (node) {
			movedNodes.push({
				node: node,
				parent: node.parentNode,
				nextSibling: node.nextSibling,
			});
			mount.appendChild(node);
		});

		modal.style.display = "flex";
		modalOpen = true;
		syncModalAtc();
		const title = document.getElementById("fitment-gate-title");
		if (title && typeof title.focus === "function") title.focus();
		LOG("modal opened; moved", movedNodes.length, "node(s)");
	}

	function restoreNodes() {
		// Put the moved fitment DOM back exactly where it came from.
		movedNodes.forEach(function (entry) {
			if (entry.nextSibling && entry.nextSibling.parentNode === entry.parent) {
				entry.parent.insertBefore(entry.node, entry.nextSibling);
			} else {
				entry.parent.appendChild(entry.node);
			}
		});
		movedNodes = [];
	}

	function closeModal() {
		const modal = document.getElementById("fitment-gate-modal");
		resolutionContinuation = null;
		if (!modal || !modalOpen) return;
		restoreNodes();
		modal.style.display = "none";
		modalOpen = false;
		if (lastFocusedElement && typeof lastFocusedElement.focus === "function") {
			lastFocusedElement.focus();
		}
		lastFocusedElement = null;
		document.dispatchEvent(new CustomEvent("partify:pdp-fitment-gate-closed"));
		LOG("modal closed; fitment restored to page");
	}

	/* ── Modal Add to Cart state ── */

	function syncModalAtc() {
		const btn = document.querySelector(".fgm-add-to-cart");
		const message = document.querySelector(".fgm-validation-message");
		const warning = document.querySelector(".fgm-fitment-warning");
		const title = document.getElementById("fitment-gate-title");
		const incompatible = isGenuinelyIncompatible();
		const sideHardStop = hasSideFitmentHardStop();
		const ready = canSubmitFromModal();
		if (btn) {
			btn.disabled = !ready;
			btn.textContent = sideHardStop
				? "Add to Cart"
				: incompatible
					? "Continue Anyway"
					: "Add to Cart";
		}
		if (title) title.textContent = incompatible ? "Fitment Warning" : "Confirm Your Fitment";
		if (warning) warning.hidden = !incompatible || sideHardStop;
		if (message) {
			message.textContent = ready ? "" : modalValidationMessage();
			message.hidden = ready;
		}
	}

	function getConfirmedVin() {
		try {
			const a = JSON.parse(localStorage.getItem("searchTerms") || "[]");
			const vin = Array.isArray(a) && a[0] ? a[0].vin : "";
			return vin ? String(vin).trim().toUpperCase() : "";
		} catch (_) {
			return "";
		}
	}

	function syncConfirmedVinToForm(form) {
		const syncVin =
			(typeof window.setupVinSync === "function" && window.setupVinSync) ||
			(typeof setupVinSync === "function" && setupVinSync);
		if (syncVin) {
			try {
				syncVin();
			} catch (_) {}
		}

		const vin = getConfirmedVin();
		if (!vin || !form) return;

		let vinInput = form.querySelector(
			'input[type="hidden"][name="properties[VIN]"]',
		);
		if (!vinInput) {
			vinInput = document.createElement("input");
			vinInput.type = "hidden";
			vinInput.name = "properties[VIN]";
			form.appendChild(vinInput);
		}
		vinInput.disabled = false;
		vinInput.value = vin;
	}

	function validCanadaVin(value) {
		return /^[A-HJ-NPR-Z0-9]{17}$/.test(
			String(value || "").trim().toUpperCase(),
		);
	}

	function getCanadaNativeFormVin(form) {
		if (!form) return "";
		const group = document.querySelector(".paint-options-checkbox-group");
		const checked = group && group.querySelector(
			'input[name="select_paint_option"]:checked',
		);
		if (checked && checked.id === "checkbox-select-unpainted") return "";

		const input = Array.from(
			form.querySelectorAll(
				'input[name="properties[VIN]"]:not([type="hidden"])',
			),
		).find(function (input) {
			return !input.disabled && validCanadaVin(input.value);
		});
		return input ? String(input.value).trim().toUpperCase() : "";
	}

	// Reviewed native/body-type failures must not turn a Garage VIN into a new
	// property requirement. Carry the exact country-origin VIN when that check is
	// required; otherwise invoke Canada's existing VIN sync only when its form
	// already carries an enabled, valid VIN field, never merely because Garage has
	// a VIN or because the shopper selected an ordinary/unpainted item.
	function syncRequiredReviewVinToForm(form) {
		if (!form) return;
		const vin = getReviewVinProperties().VIN || "";
		if (!vin) return;
		if (vin === getCanadaNativeFormVin(form)) syncConfirmedVinToForm(form);
		let vinInput = form.querySelector(
			'input[type="hidden"][name="properties[VIN]"]',
		);
		if (!vinInput) {
			vinInput = document.createElement("input");
			vinInput.type = "hidden";
			vinInput.name = "properties[VIN]";
			form.appendChild(vinInput);
		}
		vinInput.disabled = false;
		vinInput.value = vin;
	}

	function confirmFromModal() {
		if (!canSubmitFromModal()) return;
		if (isGenuinelyIncompatible()) {
			reviewAcknowledged = true;
			writeReviewPropertyToForm();
		} else {
			resetReviewAcknowledgement();
		}
		const continuation = resolutionContinuation;
		resolutionContinuation = null;
		restoreNodes();
		const modal = document.getElementById("fitment-gate-modal");
		if (modal) modal.style.display = "none";
		modalOpen = false;
		lastFocusedElement = null;
		if (continuation) {
			Promise.resolve()
				.then(function () {
					return continuation();
				})
				.catch(function (error) {
					console.error("wool [pdp-fitment-gate] continuation failed", error);
				});
			return;
		}
		LOG("modal confirm → submitting product form");
		const form = document.getElementById("product-form");
		if (!form) { LOG("ERROR: #product-form not found!"); return; }

		// requestSubmit() bypasses the ATC click handler that normally writes the
		// selected variant into input[name="id"]. For legacy quality products the
		// quality change handler only fires on user-triggered `change` events — an
		// auto-selected radio on page load never fires one, so window.selectedVariantId
		// stays at the base product's first variant. Read data-variant-id from the
		// checked quality radio directly as the source of truth.
		// For all other products (no quality radios) the Liquid template already wrote
		// the correct variant ID into the form — leave it untouched.
		const checkedQuality = document.querySelector(
			'input[type="radio"][name="quality_type"]:checked'
		);
		const qualityVariantId = checkedQuality && checkedQuality.getAttribute("data-variant-id");
		if (qualityVariantId) {
			const idInput = form.querySelector('input[name="id"]');
			if (idInput && idInput.value !== String(qualityVariantId)) idInput.value = qualityVariantId;
		}

		if (reviewAcknowledged) {
			syncRequiredReviewVinToForm(form);
		} else {
			syncConfirmedVinToForm(form);
		}

		window.__pdpFitmentGateConfirming = true;
		const selectedAssembly = document.querySelector(
			'input[name="bundle_mode"][value="kit"]:checked',
		);
		const selectedCasperAddon = document.querySelector(
			"[data-casper-bundle] [data-item-input]:checked",
		);
		const addButton = form.querySelector(
			".add-to-cart, .add-to-cart-for-unpainted",
		);
		if ((selectedAssembly || selectedCasperAddon) && addButton) {
			addButton.disabled = false;
			addButton.click();
		} else if (typeof form.requestSubmit === "function") {
			form.requestSubmit();
		} else {
			if (addButton) {
				addButton.disabled = false;
				addButton.click();
			}
		}
		setTimeout(function () {
			window.__pdpFitmentGateConfirming = false;
		}, 0);
	}

	/* ── Submit gate (capture phase, runs before the page's submit handler) ── */

	function onProductFormSubmitCapture(e) {
		const form = e.target;
		if (!form || form.getAttribute("id") !== "product-form") return;
		if (modalOpen) {
			if (!hasSideFitmentHardStop() && !countryOriginVinIncomplete()) return;
			e.preventDefault();
			e.stopImmediatePropagation();
			syncModalAtc();
			return;
		}
		if (canProceed()) return;
		if (window.__pdpFitmentGateConfirming && canSubmitWithoutVehicle()) return;
		if (isReady()) return;
		e.preventDefault();
		e.stopImmediatePropagation();
		openModal();
	}

	function onAddToCartClickCapture(e) {
		const button = e.target && e.target.closest
			? e.target.closest(ATC_BTN_SELECTOR)
			: null;
		if (
			!button ||
			window.__pdpFitmentGateConfirming ||
			canProceed() ||
			!isGenuinelyIncompatible()
		) return;
		e.preventDefault();
		e.stopImmediatePropagation();
		openModal();
	}

	/* ── Wire up ── */

	function init() {
		enableAtcButtons();
		normalizeCustomizationUI();
		enableAllAcqGroups();
		watchAtcButtons();
		watchAcqGroups();
		// Auto-select (partify-cro.js) settles asynchronously; re-assert after it.
		setTimeout(function () {
			enableAtcButtons();
			normalizeCustomizationUI();
			enableAllAcqGroups();
		}, 700);

		document.addEventListener("click", onAddToCartClickCapture, true);
		document.addEventListener("submit", onProductFormSubmitCapture, true);

		// Modal dismiss (backdrop + close button).
		const modal = document.getElementById("fitment-gate-modal");
		if (modal) {
			modal.addEventListener("click", function (e) {
				if (e.target.closest("[data-fgm-dismiss]")) {
					e.preventDefault();
					closeModal();
				}
			});
		}
		document.addEventListener("keydown", function (e) {
			if (modalOpen && e.key === "Escape") closeModal();
		});

		// Modal Add to Cart.
		const fgmBtn = document.querySelector(".fgm-add-to-cart");
		if (fgmBtn) fgmBtn.addEventListener("click", confirmFromModal);

		// Re-assert ATC state + modal button on fitment changes.
		document.addEventListener("change", function (e) {
			if (!e.target || e.target.type !== "radio") return;
			resetReviewAcknowledgement();
			enableAtcButtons();
			normalizeCustomizationUI();
			if (modalOpen) syncModalAtc();
		});
		document.addEventListener("partify:country-origin-change", function () {
			resetReviewAcknowledgement();
			enableAtcButtons();
			if (modalOpen) syncModalAtc();
		});
		document.addEventListener("partify:vin-body-type-acq-evaluated", function () {
			resetReviewAcknowledgement();
			enableAtcButtons();
			normalizeCustomizationUI();
			if (modalOpen) syncModalAtc();
		});
		window.addEventListener("partify:native-fitment-resolved", function () {
			resetReviewAcknowledgement();
			enableAtcButtons();
			normalizeCustomizationUI();
			enableAllAcqGroups();
			if (modalOpen) setTimeout(syncModalAtc, 50);
		});
		function onVehicleChanged() {
			resetReviewAcknowledgement();
			enableAtcButtons();
			if (modalOpen) syncModalAtc();
		}
		window.addEventListener("searchTermsUpdated", onVehicleChanged);
		window.addEventListener("partify:vehicle:changed", onVehicleChanged);
		window.addEventListener("storage", function (e) {
			if (e.key === "searchTerms") onVehicleChanged();
		});
		window.addEventListener("pageshow", function () {
			resetReviewAcknowledgement();
			enableAtcButtons();
			normalizeCustomizationUI();
			enableAllAcqGroups();
			if (modalOpen) syncModalAtc();
		});
		const card = document.querySelector("[data-partify-fitment-card]");
		if (card) {
			new MutationObserver(function () {
				resetReviewAcknowledgement();
				enableAtcButtons();
				normalizeCustomizationUI();
				if (
					window.__partifyCountryOriginRestorePending &&
					window.PartifyCountryOriginFitment &&
					typeof window.PartifyCountryOriginFitment.restoreWhenReady === "function"
				) window.PartifyCountryOriginFitment.restoreWhenReady();
				if (modalOpen) syncModalAtc();
			}).observe(card, { attributes: true, attributeFilter: ["data-pfc-state"] });
		}

		LOG("initialized");
	}

	window.__pdpFitmentGateIsReady = isReady;
	window.__pdpFitmentGateCanProceed = canProceed;
	window.__pdpFitmentGateIsIncompatible = isGenuinelyIncompatible;
	window.__pdpFitmentGateGetReviewProperties = getReviewProperties;
	window.__pdpFitmentGateGetReviewVinProperties = getReviewVinProperties;
	window.__pdpFitmentGateHasUnansweredAcq = hasUnansweredAcq;
	window.__pdpFitmentGateHasSideHardStop = hasSideFitmentHardStop;
	window.__pdpFitmentGateRequestResolution = requestResolution;
	// Lets an external caller (FBT's Two Colors handoff) cancel a pending Add to
	// Cart continuation and close this modal immediately — reuses the existing
	// closeModal() verbatim (already restores moved nodes + clears the stored
	// continuation); safe no-op when the modal isn't open.
	window.__pdpFitmentGateCloseModal = closeModal;

	if (document.readyState === "loading") {
		document.addEventListener("DOMContentLoaded", init);
	} else {
		init();
	}
})();
